/**
 * Operator Portal Utilities
 *
 * Shared helpers used across operator portal components and pages.
 */

// ============================================
// RUBRIC LABELS (6-point scoring scale)
// ============================================

/** 6-point rubric score to human-readable label */
export const RUBRIC_LABELS: Record<number, string> = {
  100: 'Exemplary',
  80: 'Proficient',
  60: 'Developing',
  40: 'Struggling',
  20: 'Needs Training',
  0: 'Untrained',
};

/**
 * Convert a numeric score to a rubric label.
 * Exact rubric points (0, 20, 40, 60, 80, 100) return their label directly.
 * Any other score snaps to the nearest rubric bracket so operators never see raw numbers.
 */
export function getRubricLabel(score: number): string {
  if (RUBRIC_LABELS[score]) return RUBRIC_LABELS[score];
  // Snap to nearest rubric bracket for non-standard scores
  if (score >= 90) return 'Exemplary';
  if (score >= 70) return 'Proficient';
  if (score >= 50) return 'Developing';
  if (score >= 30) return 'Struggling';
  if (score >= 10) return 'Needs Training';
  return 'Untrained';
}

/**
 * Extract initials from a user's full name.
 * Takes the first character of the first two name parts.
 * Falls back to "U" if no name is available.
 */
export function getInitials(name?: string | null): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return 'U';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Extract the first name from a full name string.
 * Uses Auth0's given_name if provided, otherwise splits the full name.
 * Falls back to 'there' if no name is available.
 */
export function getFirstName(name?: string | null, givenName?: string | null): string {
  if (givenName) return givenName;
  if (!name) return 'there';
  const first = name.trim().split(/\s+/)[0];
  return first || 'there';
}

// ============================================
// DURATION / DUE DATE FORMATTING
// ============================================

/** Format seconds to human-readable duration label (e.g. "5 min", "1h 30m") */
export function formatDuration(seconds: number | null): string | null {
  if (!seconds) return null;
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }
  return `${minutes} min`;
}

/** Format ISO date string to relative due-date label (e.g. "Due today", "3 days overdue") */
export function formatDueDate(dateString: string | null): string | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  const today = new Date();
  const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
  if (diffDays <= 7) return `Due in ${diffDays} days`;
  return `Due ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

// ============================================
// DATE FORMATTING
// ============================================

/**
 * Format ISO date string to relative label.
 * "Today", "Yesterday", weekday name (within 7 days), or "Mon DD" format.
 */
export function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();

  // Strip time for day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
